import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";

interface Invoice {
  id: number;
  invoiceNumber: string;
  status: string;
  invoiceDate: string;
  dueDate: string | null;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  currency: string;
  paidAt: string | null;
  isProration: boolean;
}

interface InvoiceDetail extends Invoice {
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  notes: string | null;
  stripeHostedInvoiceUrl: string | null;
  lineItems: Array<{
    id: number;
    description: string;
    quantity: number;
    unitAmount: number;
    amount: number;
    type: string;
    periodStart: string | null;
    periodEnd: string | null;
  }>;
}

function statusBadge(status: string): string {
  switch (status) {
    case "paid": return "bg-emerald-green-100 text-emerald-green-700";
    case "open": return "bg-cornflower-blue-100 text-cornflower-blue-700";
    case "past_due": return "bg-crimson-red-100 text-crimson-red-700";
    case "void": return "bg-slate-gray-100 text-slate-gray-500";
    default: return "bg-slate-gray-100 text-slate-gray-600";
  }
}

function fmt(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase()
  }).format(cents);
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function InvoicesPanel() {
  const api = useApi();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<InvoiceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pageSize = 20;

  const fetchInvoices = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const resp = await api.messaging.get(`admin/billing/invoices?page=${p}&pageSize=${pageSize}`);
      if (resp.ok) {
        const data = await resp.json();
        setInvoices(Array.isArray(data.invoices) ? data.invoices : []);
        setTotal(data.total ?? 0);
        setError(null);
      } else {
        setError("Failed to load invoices.");
      }
    } catch {
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { fetchInvoices(page); }, [fetchInvoices, page]);

  const openDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const resp = await api.messaging.get(`admin/billing/invoice/${id}`);
      if (resp.ok) {
        setSelected(await resp.json());
      }
    } catch {
      // ignore
    } finally {
      setDetailLoading(false);
    }
  }, [api.messaging]);

  const totalPages = Math.ceil(total / pageSize);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 border-cornflower-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-crimson-red-50 border border-crimson-red-200 rounded-lg p-4 text-sm text-crimson-red-700">
          {error}
          <button onClick={() => fetchInvoices(page)} className="ml-3 text-cornflower-blue-500 hover:text-cornflower-blue-700 font-medium">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelected(null)}
            className="flex items-center gap-1.5 text-sm text-cornflower-blue-500 hover:text-cornflower-blue-700"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to invoices
          </button>
          {selected.stripeHostedInvoiceUrl && (
            <a
              href={selected.stripeHostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-gray-200 text-xs font-medium text-slate-gray-600 hover:bg-slate-gray-50 transition-colors"
            >
              View / Download PDF
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-lg font-semibold text-slate-gray-800">{selected.invoiceNumber}</div>
              <div className="text-sm text-slate-gray-500">{fmtDate(selected.invoiceDate)}</div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusBadge(selected.status)}`}>
              {selected.status.replace("_", " ")}
            </span>
          </div>

          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="border-b border-slate-gray-200">
                <th className="text-left py-2 text-slate-gray-500 font-medium">Description</th>
                <th className="text-right py-2 text-slate-gray-500 font-medium">Qty</th>
                <th className="text-right py-2 text-slate-gray-500 font-medium">Unit</th>
                <th className="text-right py-2 text-slate-gray-500 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {selected.lineItems.map(li => (
                <tr key={li.id} className="border-b border-slate-gray-100 last:border-0">
                  <td className="py-2 text-slate-gray-700">
                    {li.description}
                    {(li.periodStart || li.periodEnd) && (
                      <div className="text-[10px] text-slate-gray-400">
                        {fmtDate(li.periodStart)} – {fmtDate(li.periodEnd)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-right text-slate-gray-600">{li.quantity}</td>
                  <td className="py-2 text-right font-mono text-slate-gray-600">{fmt(li.unitAmount, selected.currency)}</td>
                  <td className="py-2 text-right font-mono text-slate-gray-700">{fmt(li.amount, selected.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t border-slate-gray-200 pt-3 space-y-1 text-sm">
            {selected.discountAmount > 0 && (
              <div className="flex justify-between text-emerald-green-600">
                <span>Discount</span>
                <span className="font-mono">-{fmt(selected.discountAmount, selected.currency)}</span>
              </div>
            )}
            {selected.taxAmount > 0 && (
              <div className="flex justify-between text-slate-gray-600">
                <span>Tax</span>
                <span className="font-mono">{fmt(selected.taxAmount, selected.currency)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-slate-gray-800 pt-1">
              <span>Total</span>
              <span className="font-mono">{fmt(selected.totalAmount, selected.currency)}</span>
            </div>
            {selected.amountPaid > 0 && (
              <div className="flex justify-between text-emerald-green-600">
                <span>Paid {selected.paidAt ? `on ${fmtDate(selected.paidAt)}` : ""}</span>
                <span className="font-mono">-{fmt(selected.amountPaid, selected.currency)}</span>
              </div>
            )}
            {selected.amountDue > 0 && (
              <div className="flex justify-between font-semibold text-sweet-orange-600">
                <span>Balance Due</span>
                <span className="font-mono">{fmt(selected.amountDue, selected.currency)}</span>
              </div>
            )}
          </div>

          {selected.notes && (
            <div className="mt-4 text-xs text-slate-gray-500 bg-slate-gray-50 rounded p-3">
              {selected.notes}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {detailLoading && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50">
          <div className="animate-spin h-8 w-8 border-2 border-cornflower-blue-500 border-t-transparent rounded-full" />
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-gray-200">
          <h3 className="text-base font-semibold text-slate-gray-800">Invoices</h3>
          <p className="text-xs text-slate-gray-500 mt-0.5">{total.toLocaleString()} invoice{total !== 1 ? "s" : ""}</p>
        </div>

        {invoices.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-gray-400">No invoices found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-gray-50">
              <tr>
                <th className="text-left py-2.5 px-4 text-slate-gray-500 font-medium">Invoice #</th>
                <th className="text-left py-2.5 px-4 text-slate-gray-500 font-medium">Date</th>
                <th className="text-left py-2.5 px-4 text-slate-gray-500 font-medium">Status</th>
                <th className="text-right py-2.5 px-4 text-slate-gray-500 font-medium">Total</th>
                <th className="text-right py-2.5 px-4 text-slate-gray-500 font-medium">Due</th>
                <th className="py-2.5 px-4" />
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-t border-slate-gray-100 hover:bg-slate-gray-50 transition-colors">
                  <td className="py-3 px-4 font-mono text-slate-gray-700 text-xs">{inv.invoiceNumber}</td>
                  <td className="py-3 px-4 text-slate-gray-600">{fmtDate(inv.invoiceDate)}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(inv.status)}`}>
                      {inv.status.replace("_", " ")}
                    </span>
                    {inv.isProration && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-slate-gray-100 text-slate-gray-500">proration</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-slate-gray-700">{fmt(inv.totalAmount, inv.currency)}</td>
                  <td className="py-3 px-4 text-right font-mono">
                    {inv.amountDue > 0 ? (
                      <span className="text-sweet-orange-600">{fmt(inv.amountDue, inv.currency)}</span>
                    ) : (
                      <span className="text-emerald-green-600 text-xs">Paid</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => openDetail(inv.id)}
                      className="text-xs text-cornflower-blue-500 hover:text-cornflower-blue-700 font-medium"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-slate-gray-200 flex items-center justify-between">
            <span className="text-xs text-slate-gray-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded border border-slate-gray-200 text-xs text-slate-gray-600 hover:bg-slate-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded border border-slate-gray-200 text-xs text-slate-gray-600 hover:bg-slate-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
